import camelCase from 'lodash/camelCase';
import { call, put, select } from 'typed-redux-saga/macro';

import { GetActionByType } from 'Lib/reduxType';
import { processResponse } from 'Lib/reduxUtils';
import { withErrorHandling } from 'Lib/sagaHelpers';
import createKeyProcessor from 'Lib/utils/createKeyProcessor';
import { getServiceFn } from 'Services';
import { META_SERVICE } from 'Services/constants';

import { setChatSuggestionConsentsSuccess } from 'Store/appSettings/actions';
import { getAppStartInfo } from 'Store/appSettings/selectors';
import type { ChatSuggestionConsents } from 'Store/appSettings/types';
import { getPosition } from 'Store/location/selectors';
import { setSelfieVerificationStatus } from 'Store/profile/actions';

import { DEFAULT_CHECK } from '../../lib/constants/detection';
import { fetchMetaError, fetchMetaRequest, fetchMetaSuccess } from './actions';
import { FETCH_META } from './constants';
import { metaResponseSchema } from './schemas';

const processMetaResponseKeys = createKeyProcessor((key, pathParts) => {
  // Do not process lever names (data.levers.*)
  if (pathParts && pathParts.length === 2 && pathParts[1] === 'levers') {
    return key;
  }
  return camelCase(key);
});

function* setChatSuggestionConsents(chatSuggestionConsents: ChatSuggestionConsents) {
  if (chatSuggestionConsents) {
    yield* put(setChatSuggestionConsentsSuccess(chatSuggestionConsents));
  }
}

function* fetchMeta(action: GetActionByType<typeof FETCH_META>) {
  yield* put(fetchMetaRequest());
  const { sendAppStartInfo } = action.payload;
  const position = yield* select(getPosition);
  const { lat, lon } = position || {};
  const appStartInfo = yield* select(getAppStartInfo);
  const response = yield* call(
    // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
    yield* call(getServiceFn, META_SERVICE, 'fetchMeta' as const),
    {
      lat,
      lon,
    },
    {
      headers: {
        'x-tinder-start-id': sendAppStartInfo ? appStartInfo : DEFAULT_CHECK,
      },
    }
  );

  const payload = yield processResponse(response, metaResponseSchema, processMetaResponseKeys);
  yield* put(fetchMetaSuccess(payload));

  // Save chat_suggestion_consents from meta to appSettings store
  const chatSuggestionConsents = payload?.result?.chatSuggestionConsents;
  if (chatSuggestionConsents) {
    yield* setChatSuggestionConsents(chatSuggestionConsents);
  }

  // Unify selfie verification status data in the userReducer
  // Selfie verification status data is returned by the meta, profile, and updates APIs
  const selfieVerificationStatus = response?.selfieVerification?.status;

  if (selfieVerificationStatus) {
    yield* put(setSelfieVerificationStatus(selfieVerificationStatus));
  }
}

export const fetchMetaSaga = withErrorHandling(fetchMeta, {
  name: 'fetchMetaSaga',
  *errorHandler(_) {
    yield* put(fetchMetaError());
  },
});
